import React, { useEffect, useMemo, useState } from "react";
import { useWindowSize } from "@shared/hooks/useWindowSize";
import { useAuth0 } from "@auth0/auth0-react";
import {
  ListPageSkeleton,
  RefreshingIndicator,
} from "@shared/components/ListLoading";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericButton from "@ui-components/Button/GenericButton";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { CustomersModalCreate } from "./CustomersModalUpdate";
import {
  deleteCustomer,
  fetchCustomers,
  updateCustomerName,
} from "@redux/slices/tenantSlice";
import { Auth0Customer, getAuth0CustomersColumns } from "./CustomersData";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar, {
  ParsedFilter,
} from "@ui-components/Searchbar/GenericSearchbar";
import { makeCustomerFields } from "./customerFieldSpecs";
import { buildPredicate } from "kontrolEdge/helpers/advancedFilters";
import { normalizeFilters } from "kontrolEdge/helpers/normalizeFilters";
import { useRequireTenantType } from "@shared/hooks/useRequireTenantType";
import { RequireTenantType } from "@shared/components/RequireTenantType";

interface RawCustomer {
  id: string;
  customerId: string;
  name?: string;
  Name?: string;
  nameDisplay: string;
  domain: string;
  hasMFA: boolean;
  Products: Array<{
    id: number;
    name: string;
  }>;
}

type AlertConfig = {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "confirm" | "success" | "error" | "warning";
  resolve: (value: boolean) => void;
};

export function CustomersPage() {
  // Check access status to prevent unnecessary API calls
  const { isAllowed } = useRequireTenantType(["GLOBAL_ADMIN"]);
  const { tenantId } = useAppSelector((state) => state.selection);
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [isFetching, setIsFetching] = useState(false);
  const [customersData, setCustomersData] = useState<Auth0Customer[]>([]);
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  // Nothing on screen yet: the page's shape in skeleton. Once rows exist, a
  // refetch gets the thin bar instead, so the table stays readable (CTX-1122).
  const showInitialSkeleton = isFetching && customersData.length === 0;
  const isRefreshingList = isFetching && customersData.length > 0;
  const [editCustomerId, setEditCustomerId] = useState<
    string | null | undefined
  >(undefined);
  const [editedName, setEditedName] = useState<string>("");
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null);
  const dispatch = useAppDispatch();
  const customerFields = useMemo(() => makeCustomerFields(), []);

  const normalized = useMemo(
    () =>
      normalizeFilters<Auth0Customer>(activeFilters, customerFields, {
        defaultStringToContains: true,
        quotedStringsExact: true,
      }),
    [activeFilters, customerFields]
  );

  const predicate = useMemo(
    () => buildPredicate<Auth0Customer>(normalized, customerFields),
    [normalized, customerFields]
  );

  const filteredData = useMemo(
    () => customersData.filter(predicate),
    [customersData, predicate]
  );

  const suggestedKeys = useMemo(
    () => customerFields.map((f) => f.label),
    [customerFields]
  );

  const showConfirmationAlert = (
    config: Omit<AlertConfig, "resolve">
  ): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setAlertConfig({ ...config, resolve });
    });
  };

  const showAlert = (config: Omit<AlertConfig, "resolve">): Promise<void> => {
    return new Promise<void>((resolve) => {
      setAlertConfig({ ...config, resolve: () => resolve() });
    });
  };

  const fetchCustomersData = async () => {
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      const cust = await dispatch(fetchCustomers({ token, tenantId })).unwrap();
      const raw = cust.data;
      const customersList = Array.isArray(raw) ? (raw as RawCustomer[]) : [];
      const processedData = customersList.map((cust: RawCustomer) => ({
        customerId: cust.customerId,
        user_id: cust.id,
        name: cust.name || cust.Name || "",
        hasMFA: cust.hasMFA,
        nameDisplay: cust.nameDisplay,
        domain: cust.domain,
        products: cust.Products,
      }));
      setCustomersData(processedData);
      // console.log(cust.data, 'customers data');
    } catch (err) {
      console.error("Error fetching Auth0 users =>", err);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (!isAllowed) return;
    if (isLoading) return;
    if (!isAuthenticated) return;
    if (tenantId === 0) return;
    fetchCustomersData();
  }, [isAllowed, isLoading, isAuthenticated, tenantId]);

  const handleEdit = (userId: string, currentName: string) => {
    console.log("Editing user:", userId);
    setEditedName(currentName);
    setEditCustomerId(userId);
  };
  const handleSaveEdit = async (userId: string) => {
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      await dispatch(
        updateCustomerName({ token, userId, name: editedName })
      ).unwrap();
      await fetchCustomersData(); // Refresh list
    } catch (err) {
      console.error("Failed to update name:", err);
    } finally {
      setEditCustomerId(undefined);
      setEditedName("");
      setIsFetching(false);
    }
  };

  const handleCancelEdit = () => {
    setEditCustomerId(undefined);
    setEditedName("");
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async function handleDelete(userId: string) {
    const confirmed = await showConfirmationAlert({
      title: "Are you sure?",
      description: "This action will delete the customer permanently!",
      confirmLabel: "Yes, delete it!",
      cancelLabel: "Cancel",
      variant: "warning",
    });
    if (!confirmed) return;
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      console.log(tenantId, userId);
      dispatch(deleteCustomer({ token, tenantId, userId }));
      await showAlert({
        title: "Deleted!",
        description: "The customer has been removed.",
        confirmLabel: "OK",
        variant: "success",
      });
      await fetchCustomersData();
    } catch (err) {
      await showAlert({
        title: "Error!",
        description: "Failed to delete customer.",
        confirmLabel: "OK",
        variant: "error",
      });
    } finally {
      setIsFetching(false);
    }
  }
  const isPhone = useWindowSize().width < 640;
  const modifiedColumns = getAuth0CustomersColumns(
    handleEdit,
    handleDelete,
    editCustomerId,
    editedName,
    setEditedName,
    handleSaveEdit,
    handleCancelEdit,
    isPhone
  );

  if (showInitialSkeleton) {
    return (
      <RequireTenantType allowedTypes={["GLOBAL_ADMIN"]}>
        <ListPageSkeleton columns={modifiedColumns.length} />
      </RequireTenantType>
    );
  }

  return (
    <>
      <RequireTenantType allowedTypes={["GLOBAL_ADMIN"]}>
        <div className="relative">
          <RefreshingIndicator active={isRefreshingList} />
          <GenericCard
            variant="primary"
            shadow="sm"
            hasBorder
            isCollapsible={false}
            headerContent={
              <div className="flex items-center justify-end w-full">
                <div className="flex gap-3">
                  <GenericButton
                    label="Add Customer"
                    variant="brand"
                    size="md"
                    onClick={() => {
                      setIsCreatingCustomer(true);
                    }}
                  />
                </div>
              </div>
            }
            bodyContent={
              <>
                <div className="mb-4 bg-[var(--semantic-bg-primary)]">
                  <div className="w-full max-w-full sm:max-w-[520px] mx-3 sm:mx-5 my-5">
                    <GenericSearchbar
                      placeholder="Find by attribute or value"
                      suggestedKeys={suggestedKeys}
                      onFiltersChange={setActiveFilters}
                      liveFiltering
                      debounceMs={200}
                    />
                  </div>
                </div>
                <div className="px-3 sm:px-5 mb-5 sm:overflow-x-auto">
                  <GenericTable
                    columns={modifiedColumns}
                    data={filteredData}
                    mobileCards={{
                      titleColumn: "Name",
                      badgeColumn: "MFA",
                      actionColumns: ["Actions"],
                      // Display name and products in one line (CTX-1378).
                      summary: (row) => [
                        row.nameDisplay,
                        row.products?.map((p) => p.name).join(", "),
                      ],
                      sortControl: true,
                      itemNoun: "customer",
                    }}
                    initialRowsPerPage={10}
                    rowsPerPageOptions={[5, 10]}
                    width="100%"
                    maxTableBodyHeight="500px"
                    tableClassName="bg-semantic-bg-primary"
                  />
                </div>
              </>
            }
          />
        </div>
        {isCreatingCustomer && (
          <CustomersModalCreate
            setID={() => setIsCreatingCustomer(false)}
            fetchCustomers={fetchCustomersData}
          />
        )}
        {alertConfig && (
          <GenericAlert
            isOpen={true}
            onConfirm={() => {
              alertConfig.resolve(true);
              setAlertConfig(null);
            }}
            onClose={() => {
              alertConfig.resolve(false);
              setAlertConfig(null);
            }}
            title={alertConfig.title}
            description={alertConfig.description}
            confirmLabel={alertConfig.confirmLabel}
            cancelLabel={alertConfig.cancelLabel}
            variant={alertConfig.variant}
          />
        )}
      </RequireTenantType>
    </>
  );
}

export default CustomersPage;
